import { Box, CSSObject, Text } from "@mantine/core";
import React from "react";
import i18n from "i18n/i18n";

/**
 * Hero kết quả giao dịch khoản vay — bám bản design 11/08/2026
 * (`document/22-08-2026/giao-dien/GiaoDien_QuanLyKhoanVay_1108updated (1).html`).
 *
 * Design map mỗi trạng thái sang một cặp illustration + câu chữ riêng; dùng chung cho cả màn
 * kết quả của người lập (luôn PENDING) lẫn màn "Xem chi tiết giao dịch" (mọi trạng thái).
 */

export type LoanResultState =
  | "PENDING"
  | "APPROVED"
  | "SENT"
  | "SUCCESS"
  | "REJECTED"
  | "FAILED";

/** `ART_OK` — tài liệu xanh + dấu tích. */
const ArtOk: React.FC = () => (
  <svg viewBox="0 0 120 100" fill="none" width={96} height={80} aria-hidden focusable="false">
    <rect x={34} y={14} width={46} height={60} rx={5} fill="#7BD48F" />
    <rect x={41} y={24} width={24} height={4} rx={2} fill="#fff" opacity=".85" />
    <rect x={41} y={34} width={32} height={3} rx={1.5} fill="#fff" opacity=".6" />
    <rect x={41} y={42} width={32} height={3} rx={1.5} fill="#fff" opacity=".6" />
    <rect x={41} y={50} width={20} height={3} rx={1.5} fill="#fff" opacity=".6" />
    <rect x={80} y={18} width={7} height={52} rx={3.5} fill="#C7E9CE" />
    <circle cx={36} cy={64} r={15} fill="#3FB863" stroke="#fff" strokeWidth={3} />
    <path
      d="m30 64 4.5 4.5L43 60"
      stroke="#fff"
      strokeWidth={3.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * `ART_WAIT` — mũi tên gửi đi + đồng hồ chờ.
 * Bản gốc có 2 <path> đặt `stroke-width="0"` nên không vẽ ra gì, đã bỏ.
 */
const ArtWait: React.FC = () => (
  <svg viewBox="0 0 120 100" fill="none" width={96} height={80} aria-hidden focusable="false">
    <circle cx={66} cy={52} r={20} fill="#F7B733" />
    <path
      d="M60 14v14m-7-7 7-7 7 7"
      stroke="#F79E1B"
      strokeWidth={5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx={44} cy={56} r={17} fill="#F79E1B" stroke="#fff" strokeWidth={3} />
    <path d="M44 48v9l6 3.5" stroke="#fff" strokeWidth={3.2} strokeLinecap="round" />
  </svg>
);

/** `ART_ERR` — tài liệu đỏ + dấu X. */
const ArtErr: React.FC = () => (
  <svg viewBox="0 0 120 100" fill="none" width={96} height={80} aria-hidden focusable="false">
    <rect x={34} y={14} width={46} height={60} rx={5} fill="#F5A3A3" />
    <rect x={41} y={26} width={30} height={3.5} rx={1.75} fill="#fff" opacity=".8" />
    <rect x={41} y={36} width={24} height={3} rx={1.5} fill="#fff" opacity=".6" />
    <rect x={80} y={18} width={7} height={52} rx={3.5} fill="#F8C9C9" />
    <circle cx={36} cy={64} r={15} fill="#E5484D" stroke="#fff" strokeWidth={3} />
    <path d="m31 59 10 10M41 59 31 69" stroke="#fff" strokeWidth={3.4} strokeLinecap="round" />
  </svg>
);

const GRADIENT_OK = "linear-gradient(103deg,#12406F 0%,#155CA6 48%,#1E88E5 100%)";
const GRADIENT_ERR = "linear-gradient(103deg,#5C1C1C 0%,#9A2A2A 48%,#D24B4B 100%)";

/** Ba vệt chéo mờ của `.jv .hero::after`. */
const DIAGONALS = [
  "linear-gradient(115deg,transparent 44%,rgba(255,255,255,.06) 44.4%,rgba(255,255,255,.06) 45.4%,transparent 45.8%)",
  "linear-gradient(115deg,transparent 60%,rgba(255,255,255,.05) 60.4%,rgba(255,255,255,.05) 61.4%,transparent 61.8%)",
  "linear-gradient(65deg,transparent 30%,rgba(255,255,255,.05) 30.4%,rgba(255,255,255,.05) 31.2%,transparent 31.6%)",
].join(",");

const heroSx = (failed: boolean): CSSObject => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 9,
  padding: "34px 20px 30px",
  textAlign: "center",
  color: "#fff",
  background: failed ? GRADIENT_ERR : GRADIENT_OK,
  "&::after": { content: '""', position: "absolute", inset: 0, background: DIAGONALS },
});

const layer: CSSObject = { position: "relative", zIndex: 1 };

const ART: Record<LoanResultState, React.FC> = {
  PENDING: ArtWait,
  APPROVED: ArtOk,
  SENT: ArtOk,
  SUCCESS: ArtOk,
  REJECTED: ArtErr,
  FAILED: ArtErr,
};

/**
 * Câu chữ mặc định theo design. `txNoun` là "tất toán" | "thanh toán" — chỉ dùng cho SUCCESS,
 * đúng như bản design ghép động theo `req.kind`.
 */
// VersionEn: txNoun vẫn nhận CHUỖI TIẾNG VIỆT vì nơi gọi
// (manage-transaction/.../ApproveLockAccountChecker.tsx:418) suy ra nó bằng
// /tất toán/i.test(...) trên nhãn "Loại giao dịch" do máy chủ trả, và file đó không thuộc
// phần tôi được giao. Nên ở đây map từ tiếng Việt sang khoá thay vì đổi kiểu tham số:
// giữ nguyên chữ ký, nơi gọi không phải sửa, mà câu tiếng Anh vẫn ra đúng.
const TX_NOUN_KEY: Record<string, string> = {
  "tất toán": "TX_NOUN_SETTLEMENT",
  "thanh toán": "TX_NOUN_PAYMENT",
};

export const loanResultMessage = (state: LoanResultState, txNoun = "thanh toán"): string => {
  const noun = i18n.t(TX_NOUN_KEY[txNoun] ?? "TX_NOUN_PAYMENT");
  switch (state) {
    case "PENDING":
      return `${i18n.t("LOAN_RESULT_PENDING")}`;
    case "APPROVED":
      return `${i18n.t("LOAN_RESULT_APPROVED")}`;
    case "SENT":
      return `${i18n.t("LOAN_RESULT_SENT")}`;
    case "SUCCESS":
      return `${i18n.t("LOAN_RESULT_SUCCESS", { noun })}`;
    case "REJECTED":
      return `${i18n.t("LOAN_RESULT_REJECTED")}`;
    case "FAILED":
    default:
      return `${i18n.t("LOAN_RESULT_FAILED")}`;
  }
};

const LoanResultHero: React.FC<{
  state: LoanResultState;
  message: string;
  /** Đã định dạng sẵn kèm đơn vị, vd "2.851.780.822 VND". Bỏ trống thì không hiện dòng tiền. */
  amountText?: string;
}> = ({ state, message, amountText }) => {
  const Art = ART[state];
  const failed = state === "REJECTED" || state === "FAILED";
  return (
    <Box sx={heroSx(failed)}>
      <Box sx={{ ...layer, marginBottom: 14, display: "flex", justifyContent: "center" }}>
        <Art />
      </Box>
      <Text sx={{ ...layer, fontSize: 14.5, opacity: 0.95 }}>{message}</Text>
      {amountText && (
        <Text sx={{ ...layer, fontSize: 27, fontWeight: 800, marginTop: 4, letterSpacing: "-.3px" }}>
          {amountText}
        </Text>
      )}
    </Box>
  );
};

export default LoanResultHero;
